/**
 * Project KPI Config Overrides — reads/writes through the in-memory store.
 *
 * Storage:  src/Data/store.ts (in-memory Maps)
 * Durable:  PostgreSQL via API write-through
 *
 * NO localStorage reads or writes happen in this file.
 */

// DEV-963: was `import type { GUID } from "../Collections/types"` — that
// module was removed in an earlier reorg. GUID is just a string alias;
// inline as `string` here to avoid coupling to a moving import path.
import type { KpiCategoryKey, KpiChartType } from "./kpiCatalog";
import { syncProjectKpiConfigsToApi } from "../../api/kpiConfigs";
import {
  kpiConfigStore,
  isKpiConfigHydrated,
} from "../store";

export type SubKpiInstance = {
  instance_guid: string; // unique per project
  catalog_kpi_guid: string;
  display_name: string; // project-specific title (can differ from catalog)
  display_mode?: "full" | "mini";
  direction?: "increase_is_good" | "decrease_is_good" | "target_is_good";
  // DEV-963: removed duplicate `green_threshold` / `yellow_threshold` here.
  // The canonical declarations are under "Score-band thresholds" further
  // down with proper doc comments. Having them twice caused TS2300 (duplicate
  // identifier) once TS 5.4 actually parsed this file.
  variance_yellow_pp?: number;
  variance_red_pp?: number;
  schedule_template_type?: "progress" | "progress_spi";
  spi_warning_threshold?: number;
  spi_critical_threshold?: number;
  spi_min_baseline_pct?: number;
  spi_weight_pct?: number;
  weight?: number;
  is_mandatory?: boolean;
  main_chart?: KpiChartType;
  history_chart?: KpiChartType;
  data_binding_note?: string;
  // ── Mission Outcome fields ────────────────────────────────
  /** Outcome catalog reference (e.g., "OUT-TREES-PLANTED") */
  outcome_id?: string;
  /** Value type for scoring (numeric, binary, percentage, milestone_set) */
  value_type?: "numeric" | "binary" | "percentage" | "milestone_set";
  /** How values accumulate over time */
  tracking_mode?: "cumulative" | "periodic" | "one_time" | "sustained";
  /** Unit of measure (e.g., "count", "kg", "hectares", "%") */
  uom?: string;
  /** Target goal value */
  target_goal?: number;
  /** Baseline value (for decrease_is_good scoring) */
  target_baseline?: number;
  /** Reporting cadence */
  cadence?: "daily" | "weekly" | "monthly" | "once";
  /** For milestone_set: total milestones in the set */
  milestone_total?: number;
  // ── Lifecycle dates ─────────────────────────────────────────
  /** Date when tracking begins for this sub-KPI (ISO date, e.g. "2026-04-01"). Defaults to project start date. */
  tracking_start_date?: string;
  // ── Deadline / tolerance fields (all value types) ──────────
  /** Deadline date (ISO date string, e.g. "2026-06-30"). Scores degrade as deadline approaches without achievement. */
  deadline_date?: string;
  /** Days before deadline that triggers warning-level scoring (default 30) */
  warning_days?: number;
  /** Days before deadline that triggers critical-level scoring (default 7) */
  critical_days?: number;
  // ── Score-band thresholds (color derivation) ───────────────
  /** Score at or above this displays green (default 80) */
  green_threshold?: number;
  /** Score at or above this (and below green) displays yellow; below this displays red (default 60) */
  yellow_threshold?: number;
  // ── Role assignment + reporting cadence guard (DEV-814 / migration 121) ──
  /** Role responsible for entering data. NULL = unassigned. */
  assigned_role_guid?: string;
  /** Days past cadence due date before reliability flags this KPI overdue. NULL = use default. */
  permissible_days_late?: number;
  // ── Color mode (DEV-814 / migration 122; 'pace' added DEV-815 / migration 123) ──
  /**
   * 'score' (color from progress vs bands) | 'deadline' (color from time
   * remaining; score from color) | 'pace' (color from variance against
   * expected-by-today). NULL = use KPI_DEFAULTS per type.
   */
  color_mode?: 'score' | 'deadline' | 'pace';
  // ── Pace mode tolerances (DEV-815 / migration 123) ──
  /** Yellow when actual is behind expected by >= this many units. */
  pace_yellow_units_behind?: number;
  /** Red when actual is behind expected by >= this many units. Must be > yellow. */
  pace_red_units_behind?: number;
};

export type ProjectKpiConfigOverride = {
  project_guid: string;
  kpi_profile_guid?: string;
  category_weights?: Partial<Record<KpiCategoryKey, number>>;
  category_instances?: Partial<Record<KpiCategoryKey, SubKpiInstance[]>>;
};

/** Ensure store is warm (one-time localStorage migration if API hasn't loaded). */
function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

export function getProjectKpiConfigOverride(projectGuid: string): ProjectKpiConfigOverride | undefined {
  ensureWarm();
  const g = String(projectGuid).trim().toUpperCase();
  return kpiConfigStore.get(g) as ProjectKpiConfigOverride | undefined;
}

export function upsertProjectKpiConfigOverride(ov: ProjectKpiConfigOverride) {
  const g = String(ov.project_guid).trim().toUpperCase();

  // Merge with existing config in the store.
  const existing = kpiConfigStore.get(g);
  const merged = existing ? { ...existing, ...ov } : ov;
  kpiConfigStore.set(g, merged);

  // Immediately notify KPI components so they re-render from the
  // already-updated in-memory store. Without this, the UI waits for the
  // full API round-trip (POST → refetch → hydrateKpiConfigs) which can
  // take 10–30+ seconds and makes the app feel broken.
  //
  // DEV-814 fix: also dispatch when ONLY category_weights change. Previously
  // this branch was gated on category_instances, so weight edits never
  // triggered a Summary recalc until the next reload.
  if (ov.category_instances || ov.category_weights) {
    try { window.dispatchEvent(new CustomEvent("l4e_kpi_config_changed")); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("l4e_schedule_progress_changed")); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("l4e_finance_cost_changed")); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("l4e_mission_updated")); } catch { /* ignore */ }
  }

  // Write-through to API (fire-and-forget). Instances go through the
  // KpiConfigs endpoint; weights go through the Project endpoint via
  // setProjectOverride (called separately by ProjectConfigModal.handleSave).
  if (ov.category_instances) {
    syncProjectKpiConfigsToApi(String(ov.project_guid), ov.category_instances as any).catch((err) => {
      console.warn("[projectKpiConfigOverrides] API write-through failed:", err?.message || err);
    });
  }
}
